// CETRA-38: keep legacy effect timing while clearing release ESLint warnings.
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from 'react';
import { AppBar, Toolbar, useMediaQuery, useTheme, Box, Button, IconButton, Tooltip, Snackbar } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ASPrintIcon from '../../../assets/icons/Print.svg';
import ASReferencesIcon from '../../../assets/icons/References.svg';
import ASShareIcon from '../../../assets/icons/ShareHandoutFlight.svg';
import ASAddToBundleIcon from '../../../assets/icons/AddToBundle.svg';
import ASSaveDiskIcon from '../../../assets/icons/saveDisk.svg';
import ASCustomiseIcon from '../../../assets/icons/CustomiseHandout.svg';
import PatientDetailsDialog from './patient-details-dialog';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import mixpanel from 'mixpanel-browser';
import { useGenerateLink } from './use-generated-link';
import { toJS } from 'mobx';
import UserApi from '../../../rest-api/user-api';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import CTAUpgradeDialog from '../../UIControls/CTAUpgradeDialog';
import ctaUpgradeProfessionalPaywall from '../../../assets/banners/ctaUpgradeProfessionalPaywall.png';
import { getCTAUpgradeImageUrl } from '../../../config';
import { useUnsavedChanges } from '../UnsavedChangesContext';
import QuickShareDialog from './quick-share-dialog';
import { getHandoutBundleUpgradeTarget } from '../../../utils/subscription-plan-utils';

/** Pill-style action button (pink) */
const pillButtonSx = {
    height: 50,
    padding: '15px 20px',
    borderRadius: '200px',
    backgroundColor: '#FF669B',
    color: '#FFFFFF',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '18px',
    lineHeight: '18px',
    letterSpacing: '-0.36px',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    minWidth: 'auto',
    boxShadow: '0px 0px 10px rgba(35, 7, 88, 0.1)',
    '&:hover': { backgroundColor: '#e65588' },
    '&.Mui-disabled': { backgroundColor: '#FF669B', color: '#FFFFFF', opacity: 0.5 },
};

/** Pill-style action button active state (navy) — used for Customise when enabled */
const activeCustomisePillSx = {
    ...pillButtonSx,
    backgroundColor: '#000453',
    '&:hover': { backgroundColor: '#000453' },
};

/** Circular 50 x 50 icon button (pink background) */
const iconCircleButtonSx = {
    width: 50,
    height: 50,
    borderRadius: '50%',
    backgroundColor: '#FF669B',
    padding: 0,
    '&:hover': { backgroundColor: '#e65588' },
    '&:disabled': { backgroundColor: '#F0EFF1' },
};

function HandoutActions({
    handoutData,
    onToggleAuthorsAndReferences,
    bundledHandoutsView = false,
    showLabels,
    show3DModel = true,
    showMainIllustration = true,
    showTutorialVideo = true,
    is3DViewerOpen = false,
    isDiagramOnly = false,
    is3DModelOnly = false,
    embedMode = false,
    allowAuthorsButton = true,
    showWhiteboard = false,
    customisedHandoutData = null,
    // Customise mode props (desktop bar)
    isCustomiseMode = false,
    onToggleCustomise,
    onSaveHandout,
    saveHandoutDisabled = false,
    saveHandoutLabel = 'Save handout',
}) {
    const { authStore, userDataStore, whiteboardStore, customisedHandoutStore } = useContext(StoreContext);
    const { hasUnsavedChanges, promptBeforeAction } = useUnsavedChanges();
    const lastWhiteboardState = localStorage.getItem('lastWhiteboardState');
    const whiteboardData = lastWhiteboardState ? JSON.parse(lastWhiteboardState) : null;
    const { shouldShowPatientDialog } = useGenerateLink(handoutData, bundledHandoutsView, whiteboardData);
    const navigate = useNavigate();
    const theme = useTheme();
    const [currentUser, setCurrentUser] = useState(null);
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarStatus, setSnackbarStatus] = useState('success');
    const [upgradeCTAOpen, setUpgradeCTAOpen] = useState(false);
    const [upgradeCTAClinicOpen, setUpgradeCTAClinicOpen] = useState(false);
    const [patientDialogOpen, setPatientDialogOpen] = useState(false);
    const [pendingShareAction, setPendingShareAction] = useState(null);
    const [quickShareOpen, setQuickShareOpen] = useState(false);

    const shouldShowAuthorsButton = embedMode
        ? allowAuthorsButton
        : !bundledHandoutsView && !is3DViewerOpen && !isDiagramOnly && !is3DModelOnly;
    const isEditedHandoutView = Boolean(customisedHandoutData?.hasCustomisedContent || handoutData?.customisedId);
    const canShowAddToBundle = !bundledHandoutsView && !embedMode && !showWhiteboard && !isEditedHandoutView;

    useEffect(() => {
        if (embedMode) return;
        const fetchCurrentUser = async () => {
            try {
                const user = await Auth.currentAuthenticatedUser();
                setCurrentUser(user);
            } catch (error) {
                console.error('Error fetching current user: ', error);
            }
        };
        fetchCurrentUser();
    }, []);

    // --- Print ---

    const handlePrint = async () => {
        // Check Free/Student account BEFORE unsaved changes
        if (
            userDataStore?.subscriptionData?.accountType === 'Free' ||
            userDataStore?.subscriptionData?.accountType === 'Student'
        ) {
            setUpgradeCTAOpen(true);
            return;
        }

        const executePrint = async () => {
            // Check if we should show patient dialog (My Library content)
            if (shouldShowPatientDialog()) {
                setPendingShareAction('print');
                setPatientDialogOpen(true);
                return;
            }
            await executePrintPDF();
        };

        if (hasUnsavedChanges) {
            promptBeforeAction(
                () => {
                    if (onSaveHandout) {
                        onSaveHandout({ afterSave: executePrint });
                    } else {
                        executePrint();
                    }
                },
                "Your changes haven't been saved. Do you want to save before printing?",
                { preserveUnsavedChanges: true },
            );
        } else {
            await executePrint();
        }
    };

    const executePrintPDF = async (patientDetails = null) => {
        trackHandoutShared('Print Handout');

        if (!patientDetails && !shouldShowPatientDialog()) {
            try {
                const customisedHandoutState = localStorage.getItem('lastCustomisedHandoutState');
                if (customisedHandoutState) {
                    const chState = JSON.parse(customisedHandoutState);
                    if (chState.customisedId && chState.patientDetails) {
                        patientDetails = chState.patientDetails;
                    }
                }
                if (!patientDetails) {
                    const whiteboardState = localStorage.getItem('lastWhiteboardState');
                    if (whiteboardState) {
                        const wbState = JSON.parse(whiteboardState);
                        if (wbState.whiteboardId && wbState.patientDetails) {
                            patientDetails = wbState.patientDetails;
                        }
                    }
                }
            } catch (error) {
                console.error('Failed to load patient details from state:', error);
            }
        }

        handleViewHandoutPDF(patientDetails);
    };

    const handleViewHandoutPDF = (patientDetails = null) => {
        trackHandoutShared('View Handout PDF');
        const url = new URL(window.location.origin + `/handout/${handoutData.contentId}/pdf`);
        url.searchParams.append('showLabels', showLabels);

        let whiteboardId = null;
        let customisedId = null;

        // Check if handoutData itself is a customised handout (has customisedId property)
        if (handoutData.customisedId) {
            customisedId = handoutData.customisedId;
            url.searchParams.append('customisedId', customisedId);
        } else {
            // Fall back to localStorage for customised handout state
            const customisedHandoutState = localStorage.getItem('lastCustomisedHandoutState');
            if (customisedHandoutState) {
                try {
                    const chState = JSON.parse(customisedHandoutState);
                    if (chState.customisedId) {
                        customisedId = chState.customisedId;
                        url.searchParams.append('customisedId', customisedId);
                    }
                } catch (error) {
                    console.error('Failed to parse customised handout state:', error);
                }
            }
        }

        // Check if handoutData itself is a whiteboard (has whiteboardId property)
        if (handoutData.whiteboardId) {
            whiteboardId = handoutData.whiteboardId;
            url.searchParams.append('whiteboardId', whiteboardId);
            // Extract patient details from whiteboard if not provided
            if (!patientDetails && (handoutData.patientName || handoutData.patientSurname)) {
                patientDetails = {
                    patientName: handoutData.patientName,
                    patientSurname: handoutData.patientSurname,
                };
            }
        } else {
            // Fall back to localStorage for whiteboard state
            const whiteboardState = localStorage.getItem('lastWhiteboardState');
            if (whiteboardState) {
                try {
                    const wbState = JSON.parse(whiteboardState);
                    if (wbState.whiteboardId) {
                        whiteboardId = wbState.whiteboardId;
                        url.searchParams.append('whiteboardId', whiteboardId);
                    }
                } catch (error) {
                    console.error('Failed to parse whiteboard state:', error);
                }
            }
        }

        // If we have a whiteboardId but no patient details yet, try to get them from the store
        if (whiteboardId && !patientDetails && whiteboardStore?.whiteboardData) {
            const whiteboardObj = whiteboardStore.whiteboardData[whiteboardId];
            if (whiteboardObj && (whiteboardObj.patientName || whiteboardObj.patientSurname)) {
                patientDetails = {
                    patientName: whiteboardObj.patientName,
                    patientSurname: whiteboardObj.patientSurname,
                };
            }
        }

        // If we have a customisedId but no patient details yet, try to get them from the store
        if (customisedId && !patientDetails && customisedHandoutStore?.customisedHandoutData) {
            const customisedObj = customisedHandoutStore.customisedHandoutData[customisedId];
            if (customisedObj && (customisedObj.patientName || customisedObj.patientSurname)) {
                patientDetails = {
                    patientName: customisedObj.patientName,
                    patientSurname: customisedObj.patientSurname,
                };
            }
        }

        // Pass patient details via URL parameters if provided
        if (patientDetails) {
            if (patientDetails.patientName) {
                url.searchParams.append('patientName', patientDetails.patientName);
            }
            if (patientDetails.patientSurname) {
                url.searchParams.append('patientSurname', patientDetails.patientSurname);
            }
        }

        window.open(url.toString(), '_blank');
    };

    const handlePatientDetailsSubmit = async (patientDetails) => {
        setPatientDialogOpen(false);
        try {
            if (pendingShareAction === 'print') {
                await executePrintPDF(patientDetails);
            }
        } catch (error) {
            console.error('Error executing share action:', error);
        } finally {
            setPendingShareAction(null);
        }
    };

    const trackHandoutShared = (shareMethod) => {
        mixpanel.track('HandoutShared', {
            email: currentUser?.attributes?.email,
            shareMethod: shareMethod,
            handoutTitle: handoutData.contentName,
            handoutType: is3DModelOnly
                ? '3D Model'
                : isDiagramOnly
                  ? 'Diagram'
                  : showWhiteboard
                    ? 'Whiteboard'
                    : 'Handout',
        });
    };

    // --- Add to bundle ---

    const flattenCategories = (categories) => {
        let result = [];
        function traverse(currentObject) {
            if (typeof currentObject === 'string') {
                result.push(currentObject);
            } else if (Array.isArray(currentObject)) {
                currentObject.forEach((item) => {
                    if (typeof item === 'object') traverse(item);
                    else result.push(item);
                });
            } else {
                for (const key in currentObject) {
                    result.push(key);
                    traverse(currentObject[key]);
                }
            }
        }
        traverse(categories);
        return result.join(', ');
    };

    const handleAddToBundledHandouts = async () => {
        let isMultiUser = userDataStore?.subscriptionData?.accountType === 'Clinic-multiuser';
        const upgradeTarget = getHandoutBundleUpgradeTarget(userDataStore?.subscriptionData?.accountType);
        if (upgradeTarget === 'professional') {
            setUpgradeCTAOpen(true);
            return;
        }
        if (upgradeTarget === 'clinic') {
            setUpgradeCTAClinicOpen(true);
            return;
        }
        console.warn(toJS(handoutData));
        console.warn(toJS(userDataStore));

        // Handle case where handouts may be undefined
        const userHandoutsJson = userDataStore.userData.handouts || '[]';
        let userHandouts = JSON.parse(userHandoutsJson);
        let formattedCategories = flattenCategories(handoutData.categories);
        let existingContentIds = userHandouts.map((handout) => handout.contentId);

        // Check if contentId already exists to prevent duplicates
        if (existingContentIds.includes(handoutData.contentId)) {
            setSnackbarMessage('Handout already exists in the bundle');
            setSnackbarStatus('error');
            setSnackbarOpen(true);
            return;
        }

        if (userHandouts.length >= 5) {
            setSnackbarMessage('Cannot add more than 5 handouts to the bundle');
            setSnackbarStatus('error');
            setSnackbarOpen(true);
            return;
        }

        userHandouts.push({
            contentId: handoutData.contentId,
            contentName: handoutData.contentName,
            categories: formattedCategories,
        });

        const payload = { handouts: userHandouts };
        console.warn(payload);

        try {
            if (isMultiUser) {
                payload.action = 'updateMultiuser';
                payload.emailAddress = userDataStore.userData.emailAddress;
                payload.inviteId = userDataStore.userData.inviteId;
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
            } else {
                await UserApi.updateClinicianData(authStore.idToken, payload);
            }
            // After successful API call, update the local user handouts
            userDataStore.updateUserHandouts(userHandouts);
            setSnackbarMessage('Handout added to bundle');
            setSnackbarStatus('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error adding handout to bundle: ', error);
            setSnackbarMessage('Failed to add handout to bundle');
            setSnackbarStatus('error');
            setSnackbarOpen(true);
        }
    };

    // --- Share handout (opens QuickShareDialog — same as library card share button) ---

    const handleShareHandoutClick = () => {
        // Check Free/Student account BEFORE unsaved changes
        if (
            userDataStore?.subscriptionData?.accountType === 'Free' ||
            userDataStore?.subscriptionData?.accountType === 'Student'
        ) {
            setUpgradeCTAOpen(true);
            return;
        }

        const executeShare = () => {
            setQuickShareOpen(true);
        };

        if (hasUnsavedChanges) {
            // "Yes" opens the save dialog first; user can share after saving
            promptBeforeAction(
                () => {
                    if (onSaveHandout) {
                        onSaveHandout({ afterSave: executeShare });
                    } else {
                        executeShare();
                    }
                },
                "Your changes haven't been saved. Do you want to save before sharing?",
                { preserveUnsavedChanges: true },
            );
        } else {
            executeShare();
        }
    };

    // --- Customise toggle ---

    const handleCustomiseClick = () => {
        if (onToggleCustomise) {
            onToggleCustomise(null, isDiagramOnly ? !showWhiteboard : !isCustomiseMode);
        }
    };

    const getSnackbarIcon = () =>
        snackbarStatus === 'success' ? (
            <CheckIcon sx={{ ml: 1, color: 'white' }} />
        ) : (
            <CloseIcon sx={{ ml: 1, color: 'red' }} />
        );

    const mobilePillButtonSx = {
        height: 47,
        borderRadius: '188px',
        backgroundColor: '#FF669B',
        color: '#FFFFFF',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '9.4px',
        px: '18.8px',
        fontFamily: 'Inter',
        fontWeight: 500,
        fontSize: '14px',
        lineHeight: '14px',
        letterSpacing: '-0.28px',
        textTransform: 'none',
        whiteSpace: 'nowrap',
        minWidth: 'auto',
        boxShadow: '0px 0px 9.4px rgba(35, 7, 88, 0.1)',
        '&:hover': { backgroundColor: '#e65588' },
        '&.Mui-disabled': {
            backgroundColor: '#FF669B',
            color: '#FFFFFF',
            opacity: 0.65,
            '& img': {
                opacity: 1,
            },
        },
    };

    const mobileIconCircleSx = {
        width: 47,
        height: 47,
        borderRadius: '50%',
        backgroundColor: '#FF669B',
        padding: 0,
        '&:hover': { backgroundColor: '#e65588' },
    };

    return (
        <>
            <PatientDetailsDialog
                open={patientDialogOpen}
                onClose={() => {
                    setPatientDialogOpen(false);
                    setPendingShareAction(null);
                }}
                onSubmit={handlePatientDetailsSubmit}
            />

            <QuickShareDialog open={quickShareOpen} onClose={() => setQuickShareOpen(false)} handout={handoutData} />

            <CTAUpgradeDialog
                open={upgradeCTAOpen}
                handleClose={() => setUpgradeCTAOpen(false)}
                onConfirm={() => navigate('/settings/manage-subscription')}
                title="Unlock unlimited sharing and more with just one click!"
                message="You've reached a feature that's beyond your current plan. Upgrade today to enjoy full access!"
                buttonText="Upgrade plan"
                benefits={[
                    'Unlimited digital sharing of all handouts',
                    'Unlimited digital sharing of all anatomy diagrams',
                    'Personalise with your clinic details ',
                ]}
                imageUrl={ctaUpgradeProfessionalPaywall}
            />
            <CTAUpgradeDialog
                open={upgradeCTAClinicOpen}
                handleClose={() => setUpgradeCTAClinicOpen(false)}
                onConfirm={() => navigate('/settings/manage-subscription')}
                title="Upgrade now for clinic-wide access and advanced features!"
                message="You've reached a feature that's beyond your current plan. Upgrade today to add more users and enjoy full access to our features!"
                buttonText="Upgrade plan"
                benefits={[
                    'Unlimited digital sharing of all handouts & anatomy diagrams',
                    'Bundling of handouts and diagrams for seamless sharing',
                    'Personalise with your clinic logo',
                ]}
                imageUrl={getCTAUpgradeImageUrl('clinic')}
            />

            {/* Desktop action bar */}
            {!isTablet && (
                <Box
                    sx={{
                        width: '100%',
                        padding: '0 40px',
                        height: 82,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: 'white',
                        borderTop: '1px solid #e5e5e5',
                        '@media print': { display: 'none' },
                    }}
                    className="handout-actions-container"
                >
                    {/* Left group: Share handout + icon-circle buttons */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {!bundledHandoutsView && !embedMode && (
                            <Button onClick={handleShareHandoutClick} sx={pillButtonSx}>
                                <img src={ASShareIcon} alt="" style={{ width: 22, height: 22 }} />
                                Share handout
                            </Button>
                        )}

                        {/* Add to bundle — icon circle */}
                        {canShowAddToBundle && (
                            <Tooltip title="Add handout to bundle" arrow>
                                <IconButton onClick={handleAddToBundledHandouts} sx={iconCircleButtonSx}>
                                    <img
                                        src={ASAddToBundleIcon}
                                        alt="Add to bundle"
                                        style={{ width: 22, height: 22 }}
                                    />
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Print — icon circle */}
                        {!bundledHandoutsView && !is3DModelOnly && !is3DViewerOpen && (
                            <Tooltip title="Print" arrow>
                                <IconButton aria-label="Print handout" onClick={handlePrint} sx={iconCircleButtonSx}>
                                    <img
                                        src={ASPrintIcon}
                                        alt="Print"
                                        style={{ width: 24, height: 24, filter: 'brightness(0) invert(1)' }}
                                    />
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Authors — icon circle */}
                        {shouldShowAuthorsButton && (
                            <Tooltip title="Authors and references" arrow>
                                <IconButton onClick={onToggleAuthorsAndReferences} sx={iconCircleButtonSx}>
                                    <img
                                        src={ASReferencesIcon}
                                        alt="Authors"
                                        style={{ width: 24, height: 24, filter: 'brightness(0) invert(1)' }}
                                    />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>

                    {/* Right group: Save handout (hidden) + Customise */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {/* Save handout — saves whiteboard or customised handout depending on active mode */}
                        <Button
                            data-testid="handout-save"
                            onClick={onSaveHandout}
                            disabled={!onSaveHandout || saveHandoutDisabled}
                            sx={pillButtonSx}
                        >
                            <img src={ASSaveDiskIcon} alt="" style={{ width: 20, height: 22 }} />
                            {saveHandoutLabel}
                        </Button>

                        {/* Customise — active state switches to navy */}
                        {!bundledHandoutsView && !embedMode && onToggleCustomise && !is3DModelOnly && (
                            <Button
                                data-testid="handout-customise"
                                onClick={handleCustomiseClick}
                                sx={
                                    (isDiagramOnly ? showWhiteboard : isCustomiseMode)
                                        ? activeCustomisePillSx
                                        : pillButtonSx
                                }
                            >
                                <img src={ASCustomiseIcon} alt="" style={{ width: 22, height: 22 }} />
                                Customise
                            </Button>
                        )}
                    </Box>
                </Box>
            )}

            {/* Mobile / tablet top action bar */}
            {isTablet && (
                <Box
                    sx={{
                        width: '100%',
                        height: '85px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: '19px',
                        backgroundColor: '#FFFFFF',
                        borderTop: '1px solid #E5E5E5',
                        borderBottom: '1px solid #E5E5E5',
                        '@media print': { display: 'none' },
                    }}
                >
                    <Button
                        data-testid="handout-save"
                        onClick={onSaveHandout}
                        disabled={!onSaveHandout || saveHandoutDisabled}
                        sx={{ ...mobilePillButtonSx, width: '150.782px' }}
                    >
                        <img src={ASSaveDiskIcon} alt="" style={{ width: 14.8, height: 17 }} />
                        {saveHandoutLabel}
                    </Button>
                    {!bundledHandoutsView && !embedMode && onToggleCustomise && !is3DModelOnly && (
                        <Button
                            data-testid="handout-customise"
                            onClick={handleCustomiseClick}
                            sx={{
                                ...mobilePillButtonSx,
                                width: '134px',
                                backgroundColor: (isDiagramOnly ? showWhiteboard : isCustomiseMode)
                                    ? '#000453'
                                    : '#FF669B',
                                '&:hover': {
                                    backgroundColor: (isDiagramOnly ? showWhiteboard : isCustomiseMode)
                                        ? '#000453'
                                        : '#e65588',
                                },
                            }}
                        >
                            <img src={ASCustomiseIcon} alt="" style={{ width: 17, height: 17 }} />
                            Customise
                        </Button>
                    )}
                </Box>
            )}

            {/* Mobile / tablet bottom action bar */}
            {isTablet && (
                <AppBar
                    position="fixed"
                    color="default"
                    sx={{
                        top: 'auto',
                        bottom: 0,
                        backgroundColor: 'white',
                        boxShadow: 'none',
                        borderTop: '1px solid #E5E5E5',
                        px: '19px',
                        '@media print': { display: 'none' },
                    }}
                >
                    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', height: 85, px: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {!embedMode && !bundledHandoutsView && (
                                <Button
                                    onClick={handleShareHandoutClick}
                                    sx={{ ...mobilePillButtonSx, width: '158.988px' }}
                                >
                                    <img src={ASShareIcon} alt="" style={{ width: 17, height: 17 }} />
                                    Share handout
                                </Button>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {canShowAddToBundle && (
                                <Tooltip title="Add handout to bundle" arrow>
                                    <IconButton onClick={handleAddToBundledHandouts} sx={mobileIconCircleSx}>
                                        <img
                                            src={ASAddToBundleIcon}
                                            alt="Add to bundle"
                                            style={{ width: 22, height: 22 }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {!bundledHandoutsView && !is3DModelOnly && !is3DViewerOpen && (
                                <Tooltip title="Print" arrow>
                                    <IconButton
                                        aria-label="Print handout"
                                        onClick={handlePrint}
                                        sx={mobileIconCircleSx}
                                    >
                                        <img
                                            src={ASPrintIcon}
                                            alt="Print"
                                            style={{ width: 22, height: 22, filter: 'brightness(0) invert(1)' }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {shouldShowAuthorsButton && (
                                <Tooltip title="Authors and references" arrow>
                                    <IconButton onClick={onToggleAuthorsAndReferences} sx={mobileIconCircleSx}>
                                        <img
                                            src={ASReferencesIcon}
                                            alt="Authors"
                                            style={{ width: 22, height: 22, filter: 'brightness(0) invert(1)' }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Toolbar>
                </AppBar>
            )}

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                message={
                    <Box display="flex" alignItems="center">
                        {snackbarMessage}
                        {getSnackbarIcon()}
                    </Box>
                }
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{
                    '& .MuiSnackbarContent-root': {
                        backgroundImage: 'linear-gradient(132deg, #FA96C3 -2.07%, #FF669B 15.82%, #BD8DE8 116.64%)',
                        display: 'flex',
                        justifyContent: 'center',
                        borderRadius: 2,
                    },
                }}
            />
        </>
    );
}

export default HandoutActions;
